import type { Locale } from '@scoreboard/shared';
import type {
  Account,
  Attribution,
  BillingProduct,
  CreateGameInput,
  EntitlementsResponse,
  DrawResponse,
  Game,
  GameBundle,
  Participant,
  ScoreBatchResult,
  ScoreEvent,
  TargetStatus,
  Team,
  TokenPair,
  UpdateGameInput,
  VerifyResult,
} from './types.js';

// Client REST typé du contrat /v1 (doc 02 §2). `fetch` est injecté (testable, RN),
// zéro dépendance runtime hors @scoreboard/shared.
//
// Sessions (doc 02 §5.2) : l'access token vit EN MÉMOIRE (jamais localStorage), le
// refresh est un cookie HttpOnly posé par l'API. Un 401 déclenche UN refresh puis un
// rejeu unique de la requête ; un refresh en échec vide la session et notifie l'hôte.

export interface ApiErrorBody {
  code: string;
  message: string;
  requestId?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface FetchLike {
  (
    url: string,
    init?: {
      method?: string;
      headers?: Record<string, string>;
      body?: string;
      credentials?: string;
      signal?: unknown;
    },
  ): Promise<{
    ok: boolean;
    status: number;
    text(): Promise<string>;
  }>;
}

export interface ApiClientOptions {
  baseUrl: string;
  fetch: FetchLike;
  /** Session restaurée au boot (aucune persistance de l'access token côté client). */
  accessToken?: string | null;
  onTokens?: (tokens: TokenPair | null) => void;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Route publique : pas d'Authorization, pas de refresh automatique. */
  anonymous?: boolean;
  signal?: unknown;
}

export class ApiClient {
  private accessToken: string | null;
  private refreshing: Promise<boolean> | null = null;

  constructor(private readonly options: ApiClientOptions) {
    this.accessToken = options.accessToken ?? null;
  }

  get token(): string | null {
    return this.accessToken;
  }

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  private get base(): string {
    return this.options.baseUrl.replace(/\/$/, '');
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.send(path, options);
    if (response.status === 401 && options.anonymous !== true) {
      const refreshed = await this.refreshOnce();
      if (refreshed) {
        return this.parse<T>(await this.send(path, options));
      }
    }
    return this.parse<T>(response);
  }

  private async send(path: string, options: RequestOptions) {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (options.body !== undefined) {
      headers['content-type'] = 'application/json';
    }
    if (this.accessToken !== null && options.anonymous !== true) {
      headers['authorization'] = `Bearer ${this.accessToken}`;
    }
    return this.options.fetch(`${this.base}${path}`, {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include', // cookie refresh HttpOnly (doc 02 §5.2)
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
    });
  }

  private async parse<T>(response: {
    ok: boolean;
    status: number;
    text(): Promise<string>;
  }): Promise<T> {
    const raw = await response.text();
    const body: unknown = raw.length > 0 ? safeJson(raw) : undefined;
    if (!response.ok) {
      const error = (body as { error?: ApiErrorBody } | undefined)?.error;
      throw new ApiError(
        response.status,
        error?.code ?? 'HTTP_ERROR',
        error?.message ?? `HTTP ${response.status}`,
        error?.requestId,
      );
    }
    return body as T;
  }

  /** Un seul refresh en vol, partagé par toutes les requêtes qui prennent un 401. */
  private async refreshOnce(): Promise<boolean> {
    this.refreshing ??= (async () => {
      try {
        const pair = await this.parse<TokenPair>(
          await this.options.fetch(`${this.base}/v1/auth/refresh`, {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({}),
          }),
        );
        this.accessToken = pair.accessToken;
        this.options.onTokens?.(pair);
        return true;
      } catch {
        this.accessToken = null;
        this.options.onTokens?.(null);
        return false;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  // --- Auth (doc 02 §2.1) ------------------------------------------------------

  async requestMagicLink(email: string, locale: Locale): Promise<void> {
    await this.request<Record<string, never>>('/v1/auth/magic-link', {
      method: 'POST',
      body: { email, locale },
      anonymous: true,
    });
  }

  /**
   * `attribution` : origine de la boucle virale rejouée au moment du signup (doc 06
   * §7.3). Le SERVEUR décide s'il s'agit d'un signup — le client ne fait que dire
   * d'où il vient, et seulement si un compte est réellement créé.
   */
  async verifyMagicLink(token: string, attribution?: Attribution): Promise<VerifyResult> {
    const result = await this.request<VerifyResult>('/v1/auth/magic-link/verify', {
      method: 'POST',
      body: { token, ...(attribution !== undefined ? { attribution } : {}) },
      anonymous: true,
    });
    this.accessToken = result.accessToken;
    this.options.onTokens?.(result);
    // `verify` rend le refresh dans le corps (chemin mobile) ; sur le web on le
    // convertit immédiatement en cookie HttpOnly via `refresh` (doc 02 §5.2) —
    // c'est ce qui fait survivre la session à un rechargement d'onglet.
    await this.installRefreshCookie(result.refreshToken);
    return result;
  }

  private async installRefreshCookie(refreshToken: string): Promise<void> {
    try {
      const pair = await this.parse<TokenPair>(
        await this.options.fetch(`${this.base}/v1/auth/refresh`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ refreshToken }),
        }),
      );
      this.accessToken = pair.accessToken;
      this.options.onTokens?.(pair);
    } catch {
      /* le token en mémoire reste valable pour cette session */
    }
  }

  /** Restauration de session au boot : le cookie refresh suffit (pas de token stocké). */
  async refreshSession(): Promise<boolean> {
    return this.refreshOnce();
  }

  async logout(): Promise<void> {
    try {
      await this.request<void>('/v1/auth/logout', { method: 'POST', body: {} });
    } finally {
      this.accessToken = null;
      this.options.onTokens?.(null);
    }
  }

  // --- Compte (doc 02 §2.2) ----------------------------------------------------

  me(): Promise<Account> {
    return this.request<Account>('/v1/me');
  }

  updateMe(patch: { displayName?: string; locale?: Locale }): Promise<Account> {
    return this.request<Account>('/v1/me', { method: 'PATCH', body: patch });
  }

  async listGames(): Promise<Game[]> {
    const { items } = await this.request<{ items: Game[] }>('/v1/me/games');
    return items;
  }

  // --- Facturation (doc 02 §2.10) ----------------------------------------------

  /**
   * Droits RÉELS, tels que le backend les a réconciliés — jamais ce que le device
   * croit savoir (doc 01 §7.4). `GET /v1/me` porte les mêmes `plan`/`entitlements`
   * pour compatibilité, mais cette route est la source à interroger.
   */
  entitlements(): Promise<EntitlementsResponse> {
    return this.request<EntitlementsResponse>('/v1/billing/entitlements');
  }

  /** Ouvre une session Stripe Checkout ; l'appelant redirige vers `checkoutUrl`. */
  async createCheckout(input: {
    product: BillingProduct;
    business?: boolean;
    locale?: Locale;
  }): Promise<string> {
    const { checkoutUrl } = await this.request<{ checkoutUrl: string }>('/v1/billing/checkout', {
      method: 'POST',
      body: input,
    });
    return checkoutUrl;
  }

  /** Portail client Stripe : factures, moyen de paiement, résiliation. */
  async createPortalSession(locale?: Locale): Promise<string> {
    const { portalUrl } = await this.request<{ portalUrl: string }>('/v1/billing/portal', {
      method: 'POST',
      body: locale === undefined ? {} : { locale },
    });
    return portalUrl;
  }

  // --- Parties (doc 02 §2.3) ---------------------------------------------------

  async createGame(input: CreateGameInput): Promise<Game> {
    const { game } = await this.request<{ game: Game }>('/v1/games', {
      method: 'POST',
      body: input,
    });
    return game;
  }

  getGame(gameId: string): Promise<GameBundle> {
    return this.request<GameBundle>(`/v1/games/${gameId}`);
  }

  async updateGame(gameId: string, patch: UpdateGameInput): Promise<Game> {
    const { game } = await this.request<{ game: Game }>(`/v1/games/${gameId}`, {
      method: 'PATCH',
      body: patch,
    });
    return game;
  }

  async setStatus(gameId: string, status: TargetStatus): Promise<Game> {
    const { game } = await this.request<{ game: Game }>(`/v1/games/${gameId}/status`, {
      method: 'POST',
      body: { status },
    });
    return game;
  }

  async setAvatarsOnly(gameId: string, enabled: boolean): Promise<Game> {
    const { game } = await this.request<{ game: Game }>(
      `/v1/games/${gameId}/moderation/avatars-only`,
      { method: 'POST', body: { enabled } },
    );
    return game;
  }

  async resetScores(gameId: string): Promise<Game> {
    const { game } = await this.request<{ game: Game }>(`/v1/games/${gameId}/reset-scores`, {
      method: 'POST',
      body: {},
    });
    return game;
  }

  deleteGame(gameId: string): Promise<void> {
    return this.request<void>(`/v1/games/${gameId}`, { method: 'DELETE' });
  }

  // --- Équipes (doc 02 §2.4) ---------------------------------------------------

  async createTeam(gameId: string, input: { name: string; color?: string }): Promise<Team> {
    const { team } = await this.request<{ team: Team }>(`/v1/games/${gameId}/teams`, {
      method: 'POST',
      body: input,
    });
    return team;
  }

  async updateTeam(
    gameId: string,
    teamId: string,
    patch: { name?: string; color?: string },
  ): Promise<Team> {
    const { team } = await this.request<{ team: Team }>(`/v1/games/${gameId}/teams/${teamId}`, {
      method: 'PATCH',
      body: patch,
    });
    return team;
  }

  deleteTeam(gameId: string, teamId: string): Promise<void> {
    return this.request<void>(`/v1/games/${gameId}/teams/${teamId}`, { method: 'DELETE' });
  }

  // --- Participants (doc 02 §2.5) ----------------------------------------------

  async listParticipants(gameId: string): Promise<Participant[]> {
    const { items } = await this.request<{ items: Participant[] }>(
      `/v1/games/${gameId}/participants`,
    );
    return items;
  }

  async assignTeam(
    gameId: string,
    participantId: string,
    teamId: string | null,
  ): Promise<Participant> {
    const { participant } = await this.request<{ participant: Participant }>(
      `/v1/games/${gameId}/participants/${participantId}`,
      { method: 'PATCH', body: { teamId } },
    );
    return participant;
  }

  kickParticipant(gameId: string, participantId: string, ban = false): Promise<void> {
    return this.request<void>(`/v1/games/${gameId}/participants/${participantId}/kick`, {
      method: 'POST',
      body: { ban },
    });
  }

  // --- Scoring (doc 02 §2.6) ---------------------------------------------------

  scoreBatch(gameId: string, events: ScoreEvent[]): Promise<ScoreBatchResult> {
    return this.request<ScoreBatchResult>(`/v1/games/${gameId}/score-events`, {
      method: 'POST',
      body: { events },
    });
  }

  /** Undo : événement compensatoire, jamais une suppression (doc 02 §2.6). */
  revertScoreEvent(gameId: string, eventId: number): Promise<{ seq: number }> {
    return this.request<{ seq: number }>(`/v1/games/${gameId}/score-events/${eventId}`, {
      method: 'DELETE',
    });
  }

  // --- Tirages (doc 02 §2.7) ---------------------------------------------------

  draw(gameId: string, pool: 'teams' | 'participants', tag: string): Promise<DrawResponse> {
    return this.request<DrawResponse>(`/v1/games/${gameId}/draws`, {
      method: 'POST',
      body: { pool, tag },
    });
  }

  resetDraw(gameId: string, tag: string): Promise<void> {
    return this.request<void>(`/v1/games/${gameId}/draws/reset`, {
      method: 'POST',
      body: { tag },
    });
  }

  // --- Résolution publique par code (doc 02 §2.3) ------------------------------

  bootstrapByCode(code: string): Promise<GameBundle & { qrSvg: string; joinUrl: string | null }> {
    return this.request(`/v1/games/by-code/${code}/bootstrap`, { anonymous: true });
  }
}

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}
